// WantlistDetailPanel: Detail-Panel der Wantlist-Seite auf DetailSidePanel-Basis.
// Erzähl-Reihenfolge (Spec §4): Die Karte selbst (Bild+Flip, Print-Wechsel,
// Preis) → Gebraucht in → Mein Besitz (Binder + physische Decks zusammengelegt,
// klickbar → eigene Prints) → Bei Freunden.
import { useState } from 'react';
import { RotateCw, Upload } from 'lucide-react';
import DetailSidePanel from '../ui/DetailSidePanel';
import SectionHeader from '../ui/SectionHeader';
import CardDetail from './CardDetail';
import { useReplacePrint, useUpdateCardMetadata } from '../hooks/useWantlistData';
import { formatCategoryName } from '../ui/formatCategoryName';
import { useCloseOnBack } from '../ui/backLayer/useCloseOnBack';
import './WantlistDetailPanel.css';

export default function WantlistDetailPanel({ card, onExportForFriend, onDismiss }) {
  // Offener Abschnitt des Karten-Modals: { section } — null heißt geschlossen.
  const [detail, setDetail] = useState(null);
  const replacePrint = useReplacePrint();
  const updateMetadata = useUpdateCardMetadata();
  // Vor dem frühen Return unten: DetailSidePanel selbst bindet nicht an (kein `open`, die
  // Seite rendert ihn unbedingt) — hier trägt der Konsument die Zusage, weil `card` (kann
  // null sein) den offenen Zustand markiert und `onDismiss` denselben Schließweg nutzt.
  useCloseOnBack(Boolean(card), onDismiss);

  if (!card) {
    // Desktop: leerer Hinweis in der Sidebar-Spalte; mobil unsichtbar (kein Sheet).
    return (
      <aside className="wantlist-detail-empty">
        <p>Karte auswählen…</p>
      </aside>
    );
  }

  const totalMissingValue = (card.price || 0) * (card.missing || 0);
  const hasPrice = card.price !== null && card.price !== undefined;

  // Mein Besitz: echte Binder + physische Decks (binderType='deck') zusammengelegt
  const storages = (card.binders || []).map((b) => ({
    name: b.name,
    quantity: b.quantity,
    isDeck: b.binderType === 'deck',
  }));

  return (
    <DetailSidePanel
      cardName={card.name}
      image={card.imageUrl}
      imageBack={card.imageUrlBack && String(card.imageUrlBack).trim() !== '' ? card.imageUrlBack : undefined}
      imageOrientation={card.imageOrientation}
      imageAction={
        <button
          type="button"
          className="btn-secondary wantlist-detail__print-btn"
          onClick={() => setDetail({ section: 'prints' })}
        >
          <RotateCw size={14} aria-hidden /> Print wechseln
        </button>
      }
      onDismiss={onDismiss}
    >
      {/* Block 1 — die Karte selbst */}
      {card.printLocked && card.setCode && (
        <div className="wantlist-detail__setcode">{card.setCode.toUpperCase()}</div>
      )}
      {hasPrice && (
        <div className="wantlist-detail__price">
          <div className="wantlist-detail__row">
            <span>Stückpreis:</span>
            <span>{card.price.toFixed(2)} €</span>
          </div>
          {card.missing > 0 && (
            <div className="wantlist-detail__row">
              <span>Fehlend gesamt:</span>
              <strong>{totalMissingValue.toFixed(2)} €</strong>
            </div>
          )}
        </div>
      )}

      {/* Block 2 — warum steht sie auf der Wantlist? */}
      {card.deckName && (
        <section className="wantlist-detail__section">
          <SectionHeader size="sm">Gebraucht in</SectionHeader>
          <div className="wantlist-detail__row">
            <span className="wantlist-detail__row-main">{card.deckName}</span>
            <span className="wantlist-detail__meta">{formatCategoryName(card.category) || '—'}</span>
            <span>{card.quantity}x</span>
          </div>
        </section>
      )}

      {/* Block 3 — Mein Besitz: klickbar wie Freunde-Chips, physische Decks in Akzent */}
      <section className="wantlist-detail__section">
        <SectionHeader size="sm">Mein Besitz</SectionHeader>
        {storages.length > 0 ? (
          storages.map((s, i) => (
            <button
              key={`${s.name}-${i}`}
              type="button"
              className="wantlist-detail__chip"
              onClick={() => setDetail({ section: 'ownership' })}
              title={`Welche Versionen liegen in ${s.name}?`}
            >
              <span className={s.isDeck ? 'wantlist-detail__storage--deck' : undefined}>
                {s.name}
              </span>
              <span className="wantlist-detail__meta">{s.quantity}x</span>
            </button>
          ))
        ) : (
          <div className="wantlist-detail__row">
            <span className="wantlist-detail__meta">Nicht in Besitz</span>
          </div>
        )}
      </section>

      {/* Block 4 — Bei Freunden */}
      {card.friends?.length > 0 && (
        <section className="wantlist-detail__section">
          <SectionHeader size="sm">Bei Freunden</SectionHeader>
          {card.friends.map((f, i) => (
            <div key={`${f.userId}-${f.name}-${i}`} className="wantlist-detail__friend-row">
              <button
                type="button"
                className="wantlist-detail__chip"
                onClick={() => setDetail({ section: 'friends' })}
                title="Freunde-Abschnitt der Karte öffnen"
              >
                <span>{f.name}</span>
                <span className="wantlist-detail__meta">{f.quantity}x</span>
              </button>
              {onExportForFriend && (
                <button
                  type="button"
                  className="btn-icon"
                  onClick={() => onExportForFriend(f.name)}
                  title={`Alle fehlenden Karten exportieren, die ${f.name} besitzt`}
                  aria-label={`Liste für ${f.name} exportieren`}
                >
                  <Upload size={14} aria-hidden />
                </button>
              )}
            </div>
          ))}
        </section>
      )}

      <CardDetail
        open={!!detail}
        onOpenChange={(next) => { if (!next) setDetail(null); }}
        oracleId={card.oracleId}
        scryfallId={card.scryfallId}
        fallback={{ oracleId: card.oracleId, name: card.name, imageUrl: card.imageUrl, imageUrlBack: card.imageUrlBack || undefined, imageOrientation: card.imageOrientation }}
        section={detail?.section}
        context="switch-print"
        printLocked={!!card.printLocked}
        onSwitchPrint={async (newScryfallId) => {
          try {
            // Wantlist enthält nur virtuelle Decks — UI-ID also "virtual_${deckId}".
            await replacePrint.mutateAsync({
              deckId: `virtual_${card.deckId}`,
              scryfallId: card.scryfallId,
              newScryfallId,
            });
            return true;
          } catch (error) {
            return false; // Fehler meldet MutationErrorToasts
          }
        }}
        // mutate statt mutateAsync: der Fehler landet im globalen Mutations-Toast, kein unbehandeltes Promise
        onUnlockPrint={() => updateMetadata.mutate({ deckCardId: card.deckCardId, metadata: { printLocked: false } })}
      />
    </DetailSidePanel>
  );
}
